import { AsyncForm } from '@components/async-form';
import { BtnPrimary } from '@components/buttons';
import { BaseDialog } from '@components/dialog';
import { useCurrentUser } from 'client/lib/auth';
import { autoFocusSelf } from 'client/utils/autofocus';
import { useState } from 'preact/hooks';

type Props = {
  hide: () => void;
  onStart: (firstName: string) => void;
};

function BenefitItem({ icon, title, bgClass }: { icon: string; title: string; bgClass: string }) {
  return (
    <div class={`flex flex-col items-center text-center py-6 px-4 rounded-2xl ${bgClass}`}>
      <p class={`mb-4 text-3xl text-white`}>{icon}</p>
      <p class="text-sm font-semibold text-gray-700 whitespace-pre">{title}</p>
    </div>
  );
}

export function StartInterviewModal({ hide, onStart }: Props) {
  const user = useCurrentUser();
  const [firstName, setFirstName] = useState(user?.displayName || user?.name || '');

  return (
    <BaseDialog onClose={hide}>
      <AsyncForm
        class="px-6 lg:px-12 py-8 lg:py-10 flex flex-col items-center overflow-y-auto text-center max-w-4xl"
        onSubmit={async () => {
          if (firstName) {
            onStart(firstName);
          }
        }}
      >
        <div class="size-20 lg:size-24 bg-gradient-to-br from-indigo-300 to-indigo-600 rounded-full flex items-center justify-center mb-8 text-3xl lg:text-5xl text-white">
          💬
        </div>
        <h1 class="text-2xl lg:text-4xl font-bold text-gray-900 mb-4">Ready for a quick chat?</h1>
        <p class="text-base lg:text-lg text-gray-500 mb-12 max-w-lg">
          I'll ask about your coaching ideas and create a landing page you can actually use.
        </p>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-2 mb-12 w-full">
          <BenefitItem icon="⏱️" title={`5 minutes of\nconversation`} bgClass="bg-blue-50" />
          <BenefitItem icon="🎯" title={`Clarify what\nyou offer`} bgClass="bg-green-50" />
          <BenefitItem icon="📄" title={`Get a real\nlanding page`} bgClass="bg-purple-50" />
        </div>

        <div class="w-full bg-gray-50 rounded-2xl p-8 mb-8 text-left border border-gray-200">
          <label class="block text-base font-medium text-gray-900 mb-4 text-left">
            What should I call you?
          </label>
          <input
            type="text"
            placeholder="Your first name"
            class="ruz-input py-3"
            ref={autoFocusSelf}
            onInput={(e) => setFirstName((e.target as HTMLInputElement).value)}
            value={firstName}
          />
        </div>

        <p class="text-xs lg:text-sm text-gray-500 mb-8 italic">
          Just find somewhere quiet to talk. We'll ask to use your mic when we start.
        </p>

        <footer>
          <BtnPrimary class="rounded-full py-4 px-8" disabled={firstName.length === 0}>
            <span class="mr-2">💬</span> Let's Chat
          </BtnPrimary>
        </footer>
      </AsyncForm>
    </BaseDialog>
  );
}
